/**
 * projectFacts - the small shared readings of a project record.
 *
 * The status/priority colours and the due-date urgency used to live inside
 * `ProjectDetail`, which was fine while everything that rendered them was in
 * that one file. The context pane reads the same three now, so they are here
 * rather than exported from the page (which the pane imports, and would make
 * the cycle).
 */

import { BadgeColor } from '../shared/TailwindComponents/types/badgeTypes';
import { parseServerDate } from '../../utils/functions';

export const statusColorMap: Record<string, BadgeColor> = {
  active: 'green',
  archived: 'gray',
  completed: 'blue',
};

export const priorityColorMap: Record<string, BadgeColor> = {
  low: 'gray',
  medium: 'yellow',
  high: 'orange',
  urgent: 'red',
};

export type DueUrgency = 'overdue' | 'soon' | 'upcoming' | 'normal';

/** How the due date reads: `null` when there is no date at all. */
export const dueUrgency = (iso: string | null | undefined): DueUrgency | null => {
  if (!iso) return null;
  const due = parseServerDate(iso);
  if (!due) return null;
  const days = Math.ceil((due.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  if (days < 0) return 'overdue';
  if (days <= 3) return 'soon';
  if (days <= 14) return 'upcoming';
  return 'normal';
};

/** Days remaining/overdue as a phrase, or null once it is far enough out. */
export const dueCountdown = (iso: string | null | undefined): string | null => {
  const urgency = dueUrgency(iso);
  if (!urgency || urgency === 'normal') return null;
  const days = Math.ceil(((parseServerDate(iso) as Date).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  return days < 0 ? `${Math.abs(days)}d overdue` : `${days}d remaining`;
};

/** The `tone` a Field/GlassCard takes for a due date. */
export const dueTone = (iso: string | null | undefined): 'default' | 'warning' | 'danger' => {
  const urgency = dueUrgency(iso);
  if (urgency === 'overdue') return 'danger';
  if (urgency === 'soon') return 'warning';
  return 'default';
};

/** "active" → "Active". Statuses and priorities arrive lowercase. */
export const titleCase = (value: string | null | undefined): string =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : '';
